import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Assets
import inventoryImage from "../assets/testimonials/Inventory.png";
import saleImage from "../assets/testimonials/sales.png";
import purchaseImage from "../assets/testimonials/purchase.png";
import inventoryPerformanceImage from "../assets/testimonials/inventory_performance.png";

const panels = [
  { id: 1, title: "Inventory", subtitle: "Real-time Tracking", img: inventoryImage },
  { id: 2, title: "Sales", subtitle: "Revenue Analytics", img: saleImage },
  { id: 3, title: "Purchase", subtitle: "Order Management", img: purchaseImage },
  { id: 4, title: "Finance", subtitle: "Profit Insights", img: inventoryPerformanceImage },
];

export default function BlushAlignedShowcase() {
  const [active, setActive] = useState(0);

  return (
    <section className="relative bg-[#02040a] py-20 sm:py-32 min-h-screen flex flex-col items-center justify-start overflow-hidden">
      
      {/* Background Glows */}
      <div className="absolute top-0 left-1/4 w-[300px] sm:w-[500px] h-[300px] sm:h-[500px] bg-blue-600/10 blur-[150px] rounded-full pointer-events-none" />
      <div className="absolute bottom-0 right-1/4 w-[400px] sm:w-[600px] h-[400px] sm:h-[600px] bg-blue-900/10 blur-[180px] rounded-full pointer-events-none" />

      <div className="max-w-[1600px] w-full px-4 sm:px-6 lg:px-10 z-10">
        
        {/* Header */}
        <div className="mb-12 sm:mb-20">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            className="flex items-center gap-4 mb-4 sm:mb-6"
          >
            <div className="h-[1px] w-10 sm:w-16 bg-gradient-to-r from-blue-600 to-transparent"></div>
            <span className="text-blue-500 font-mono text-[10px] sm:text-xs tracking-[0.3em] sm:tracking-[0.4em] uppercase font-bold">Features</span>
          </motion.div>
          <h2 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white font-bold tracking-tight px-2">
            Built for <span className="text-blue-600">Performance.</span>
          </h2>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 sm:gap-16 items-start">
          
          {/* Sidebar - Horizontal scroll on mobile */}
          <div className="lg:col-span-2 flex lg:flex-col flex-row overflow-x-auto lg:overflow-visible gap-4 sm:gap-6 pb-4 lg:pb-0 pt-2">
            {panels.map((panel, index) => (
              <div
                key={panel.id}
                onMouseEnter={() => setActive(index)}
                onClick={() => setActive(index)}
                className="group cursor-pointer relative flex-shrink-0"
              >
                <div className={`transition-all duration-500 lg:pl-8 px-4 lg:px-0 lg:border-l-2 ${
                  active === index ? "border-blue-600" : "border-white/5"
                }`}>
                  <h3 className={`text-xl sm:text-2xl lg:text-3xl font-bold transition-all duration-300 whitespace-nowrap lg:whitespace-normal ${
                    active === index ? "text-white scale-105 origin-left" : "text-white/20 group-hover:text-white/40"
                  }`}>
                    {panel.title}
                  </h3>
                  <p className={`text-xs sm:text-sm mt-1 sm:mt-2 font-medium transition-all duration-500 ${
                    active === index ? "text-blue-400 opacity-100 translate-y-0" : "text-blue-900 opacity-0 -translate-y-2 hidden lg:block"
                  }`}>
                    {panel.subtitle}
                  </p>
                </div>
                
                {active === index && (
                  <motion.div 
                    layoutId="text-glow"
                    className="absolute inset-0 bg-blue-600/5 blur-2xl -z-10 rounded-full"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Image Showcase */}
          <div className="lg:col-span-10 relative group">
            
            {/* Outer Glow */}
            <div className="absolute -inset-1 bg-gradient-to-br from-blue-600/20 via-transparent to-blue-900/30 rounded-2xl sm:rounded-[3rem] blur-sm opacity-50 transition duration-1000 group-hover:opacity-100" />
            
            {/* Main Container */}
            <div className="relative h-[350px] sm:h-[450px] md:h-[500px] lg:h-[600px] bg-[#080a12] border border-white/10 rounded-2xl sm:rounded-[3rem] overflow-hidden flex items-center justify-center p-4 sm:p-6 md:p-10 shadow-[0_0_100px_rgba(0,0,0,0.8)]">
              
              {/* Internal Glows */}
              <div className="absolute -top-20 -right-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-blue-600/10 blur-[100px] rounded-full pointer-events-none" />
              <div className="absolute -bottom-20 -left-20 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-blue-900/15 blur-[100px] rounded-full pointer-events-none" />

              <AnimatePresence mode="wait">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, scale: 0.95, filter: "blur(10px)" }}
                  animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
                  exit={{ opacity: 0, scale: 1.05, filter: "blur(10px)" }}
                  transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                  className="w-full h-full flex items-center justify-center"
                >
                  <img
                    src={panels[active].img}
                    alt={panels[active].title}
                    className="max-w-full max-h-full object-contain drop-shadow-[0_0_60px_rgba(37,99,235,0.25)] rounded-lg"
                  />
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Reflection */}
            <div className="absolute -bottom-8 sm:-bottom-12 left-1/2 -translate-x-1/2 w-[60%] sm:w-[80%] h-8 sm:h-12 bg-blue-600/10 blur-[40px] sm:blur-[60px] rounded-full opacity-50" />
          </div>
        </div>
      </div>
    </section>
  );
}